/* eslint-disable react/jsx-props-no-spreading */
// import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { Label, Button } from '@micromag/core/components';
import { Toggle, Fields } from '@micromag/fields';
import classNames from 'classnames';
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import useStoryLastPublication from '../../hooks/useStoryLastPublication';
import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/items/publication-service.module.scss';

const propTypes = {
    item: AppPropTypes.publicationService.isRequired,
    story: MicromagPropTypes.story,
    value: AppPropTypes.publicationServiceValue,
    className: PropTypes.string,
    onChange: PropTypes.func,
};

const defaultProps = {
    value: null,
    story: null,
    className: null,
    onChange: null,
};

const PublicationService = ({ item, value, story, className, onChange }) => {
    const { id } = item;
    const { id: storyId } = story || {};
    const disabled = id === 'apple-news' || id === 'google-amp'; // @TODO remove after enabling services
    const [settingsOpened, setSettingsOpened] = useState(false);
    const settingsFields = useMemo(() => {
        const { settings: fields = null } = item;
        return fields.map((it) => ({
            ...it,
            isHorizontal: true,
        }));
    }, []);
    const enabled = isObject(value) ? value.enabled || false : value || false;
    const settings = isObject(value) ? value.settings || null : null;

    const query = useMemo(() => ({ service: id }), [id]);
    const { lastPublication } = useStoryLastPublication(storyId, query, {
        disabled: !enabled,
    });

    useEffect(() => {
        if (enabled && lastPublication !== null && onChange !== null) {
            onChange({
                ...(isObject(value) ? value : null),
                enabled: true,
                settings: {
                    ...(value || {}).settings,
                    ...lastPublication.settings,
                },
            });
        }
    }, [enabled, lastPublication]);

    const onEnableChange = useCallback(
        (checked) => {
            const newValue = isObject(value)
                ? {
                      ...value,
                      enabled: checked,
                  }
                : checked;

            if (checked && !settingsOpened) {
                setSettingsOpened(true);
            }

            if (!checked && settingsOpened) {
                setSettingsOpened(false);
            }

            if (onChange !== null) {
                onChange(newValue);
            }
        },
        [id, value, settingsOpened, setSettingsOpened, onChange],
    );
    const onClickSettings = useCallback(
        () => setSettingsOpened(!settingsOpened),
        [settingsOpened, setSettingsOpened],
    );
    const onSettingsChange = useCallback(
        (newSettings) => {
            const currentEnabled = isObject(value) ? value.enabled || false : value || false;
            const newValue =
                newSettings !== null
                    ? {
                          enabled: currentEnabled,
                          settings: newSettings,
                      }
                    : currentEnabled;
            if (onChange !== null) {
                onChange(newValue);
            }
        },
        [value, onChange],
    );
    return (
        <li
            className={classNames([
                'list-group-item',
                'p-0',
                {
                    'bg-dark': settingsOpened,
                    'text-white-50': !enabled,
                    [className]: className !== null,
                    disabled,
                },
            ])}
        >
            <div className="p-2 px-3">
                <div className="row align-items-center mx-n2">
                    <div className={classNames(['col', 'px-2'])}>
                        <Label>{item.name}</Label>
                    </div>
                    {settingsFields !== null && settingsFields.length && !disabled ? (
                        <div className="col-auto px-2">
                            <Button
                                theme="secondary"
                                size="sm"
                                outline
                                onClick={onClickSettings}
                                disabled={!enabled}
                                className={classNames({
                                    active: settingsOpened,
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Settings"
                                    description="Button label"
                                />
                            </Button>
                        </div>
                    ) : null}
                    {!disabled ? (
                        <div className="col-auto px-2">
                            <Toggle value={enabled} onChange={onEnableChange} />
                        </div>
                    ) : (
                        <small>
                            <FormattedMessage
                                defaultMessage="Coming soon"
                                description="Coming soon hint"
                            />
                        </small>
                    )}
                </div>
            </div>
            {settingsFields !== null ? (
                <div
                    className={classNames([
                        styles.settings,
                        {
                            'd-none': !settingsOpened,
                        },
                    ])}
                >
                    <Fields
                        fields={settingsFields}
                        value={settings}
                        isList
                        isFlushList
                        labelClassName={styles.label}
                        onChange={onSettingsChange}
                    />
                </div>
            ) : null}
        </li>
    );
};

PublicationService.propTypes = propTypes;
PublicationService.defaultProps = defaultProps;

export default PublicationService;
